import { api } from './api';

// Web mirror of the Stage-3 Foundation-0 contract. The Control Tower and every future
// operational board consume THESE — no bespoke per-board APIs (frozen architecture).

export type DiaryScope = 'mine' | 'supported' | 'chambers' | 'court';

export interface DiaryItem {
  id: string;
  type: string;
  title: string;
  startAt: string;
  endAt: string;
  allDay: boolean;
  location: string | null;
  lifecycle: string;
  owner: { id: string; name: string } | null;
  matter: {
    id: string; number: string; name: string; status: string; priority: string;
    client: string | null; court: string | null; opponent: string | null;
  } | null;
  hearing: { id: string; type: string; status: string; judge: string | null; courtroom: string | null } | null;
  readiness: { percentage: number; status: string; missingCount: number } | null;
  obligations: { total: number; blocked: number; ready: number } | null;
  money: { unbilled: boolean; outstanding: boolean } | null;
}

export type DiaryWarningKind = 'double_book' | 'blocked_obligation' | 'missing_prep' | 'overdue_fee' | 'dormant_matter';

export interface DiaryWarning {
  kind: DiaryWarningKind;
  severity: 'info' | 'watch' | 'act';
  subjectType: 'hearing' | 'matter' | 'event';
  subjectId: string;
  title: string;
  detail: string;
  evidence: string[];
}

export interface OperationsQuery {
  scope: DiaryScope;
  from?: string;
  to?: string;
  courtId?: string;
}

export async function fetchOperationsDiary(q: OperationsQuery): Promise<DiaryItem[]> {
  const res = await api.get('/operations/diary', { params: q });
  return (res.data as any).data ?? [];
}

export async function fetchDiaryIntelligence(q: OperationsQuery): Promise<DiaryWarning[]> {
  const res = await api.get('/operations/intelligence', { params: q });
  return (res.data as any).data ?? [];
}

// ─── Coverage Board ───────────────────────────────────────────────
export type CoverageLoad = 'overloaded' | 'busy' | 'light';

export interface CoverageDay {
  date: string;
  hearings: number;
  onLeave: boolean;
}

export interface CoverageBarrister {
  userId: string;
  name: string;
  barristerId: string | null;
  totalHearings: number;
  prepGaps: number;
  days: CoverageDay[];
  leaveDays: string[];
  load: CoverageLoad;
}

export interface CoverageResult {
  from: string;
  to: string;
  days: string[];
  barristers: CoverageBarrister[];
  uncovered: { id: string; date: string; title: string; matter: string | null; court: string | null }[];
}

export async function fetchCoverage(q: OperationsQuery): Promise<CoverageResult> {
  const res = await api.get('/operations/coverage', { params: q });
  return (res.data as any).data;
}
